/**
 * Domain pack yuklash va ro'yxatga olish.
 * Engine kodida pack'larga havola YO'Q: kim chaqirsa, o'sha pack'ni beradi.
 */
import { registerSubstances, type Substance } from '../properties';
import { registerLabs } from '../labs';
import { parseLab, type LabDefinition } from '../scenario/schema';
import { fillCyrillic } from '../scenario/localize';
import { parseFlowsheet, registerFlowsheets, type Flowsheet } from '../flowsheet';
import { parseReaction, registerReactions, type ReactionDef } from '../reactions';
import { PackManifest, SubstanceSchema, type RawPack } from './schema';
import { parseBenchLab, type BenchLab } from '../bench/schema';
import { fillCyrillicBench } from '../bench/localize';
import { registerBenchLabs } from '../bench/labs';

export * from './schema';

const LOADED = new Map<string, Pack>();

/** Xom pack'larni bog'liqlik tartibida saralash (dependencies avval) */
export function orderRawPacks<T extends { manifest: unknown }>(raws: T[]): T[] {
  const byId = new Map<string, T>();
  const deps = new Map<string, string[]>();
  for (const r of raws) {
    const m = PackManifest.parse(r.manifest);
    byId.set(m.id, r);
    deps.set(m.id, m.dependencies);
  }
  const out: T[] = [];
  const seen = new Set<string>();
  const visit = (id: string, stack: string[]) => {
    if (seen.has(id)) return;
    if (stack.includes(id)) throw new Error(`Pack dependency cycle: ${[...stack, id].join(' -> ')}`);
    for (const d of deps.get(id) ?? []) if (byId.has(d)) visit(d, [...stack, id]);
    seen.add(id);
    out.push(byId.get(id) as T);
  };
  for (const id of [...byId.keys()].sort()) visit(id, []);
  return out;
}

export interface Pack {
  manifest: PackManifest;
  components: Substance[];
  labs: LabDefinition[];
  /** Umumiy kimyo stoli lablari (kind: bench) */
  benchLabs: BenchLab[];
  reactions: ReactionDef[];
  flowsheets: Flowsheet[];
  i18n: Record<string, Record<string, string>>;
}

/** Xom fayllardan tekshirilgan pack yig'ish (sof; hech narsa ro'yxatga olinmaydi) */
export function assemblePack(raw: RawPack): Pack {
  const manifest = PackManifest.parse(raw.manifest);
  for (const d of manifest.dependencies) if (!LOADED.has(d)) throw new Error(`Pack ${manifest.id} depends on ${d}, which is not loaded`);
  const components = raw.components.map((c) => SubstanceSchema.parse(c) as Substance);
  const ids = new Set(components.map((c) => c.id));
  // bog'liq pack'lar moddalari ham ko'rinadi
  for (const d of manifest.dependencies) for (const c of (LOADED.get(d) as Pack).components) ids.add(c.id);
  const known = new Set(ids);
  for (const d of manifest.dependencies) for (const r of (LOADED.get(d) as Pack).reactions) known.add(`reaction:${r.id}`);
  if (new Set(components.map((c) => c.id)).size !== components.length) throw new Error(`Pack ${manifest.id}: duplicate substance ids`);
  const isBench = (l: unknown) => !!l && typeof l === 'object' && (l as { kind?: unknown }).kind === 'bench';
  const labs = raw.labs.filter((l) => !isBench(l)).map((l) => fillCyrillic(parseLab(l)));
  const benchLabs = raw.labs.filter(isBench).map((l) => fillCyrillicBench(parseBenchLab(l)));
  const reactions = raw.reactions.map((r) => parseReaction(r));
  for (const r of reactions) {
    for (const c of Object.keys(r.stoich)) if (!ids.has(c)) throw new Error(`Pack ${manifest.id}: reaction ${r.id} uses unknown component ${c}`);
  }
  const flowsheets = raw.flowsheets.map((f) => parseFlowsheet(f));
  for (const f of flowsheets) {
    for (const c of f.components) if (!ids.has(c)) throw new Error(`Pack ${manifest.id}: flowsheet ${f.id} uses unknown component ${c}`);
  }
  return { manifest, components, labs, benchLabs, reactions, flowsheets, i18n: raw.i18n };
}

/** Pack'ni engine reyestrlariga qo'shish (moddalar, lablar, sxemalar) */
export function registerPack(pack: Pack): void {
  registerSubstances(pack.components);
  registerLabs(pack.labs);
  registerBenchLabs(pack.benchLabs);
  registerReactions(pack.reactions);
  registerFlowsheets(pack.flowsheets);
  LOADED.set(pack.manifest.id, pack);
}

export function loadedPacks(): Pack[] {
  return [...LOADED.values()];
}

export function packIds(): string[] {
  return [...LOADED.keys()];
}

/** Barcha yuklangan pack'lar bo'yicha i18n matni (kalit → matn) */
export function packText(locale: string, key: string): string | undefined {
  for (const p of LOADED.values()) {
    const v = p.i18n[locale]?.[key];
    if (v !== undefined) return v;
  }
  return undefined;
}
